import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { first } from 'rxjs/operators';
import { Country } from '../../Foundation/models/enums/country.enum';
import { CustomerManageService } from '../../customer/shared/customer-manage.service';
import { DriverAttributeTypeLookups } from '../../driver/models/credential/driver-attribute-type-lookups.model';
import { DriverCredentialTypeAttributeTypeMap } from '../../driver/models/credential/driver-credential-type-attribute-type-map.model';
import { DriverOOJCredentialRequest } from '../../driver/models/credential/driver-ooj-credential-request.model';
import { DriverOOJCredential } from '../../driver/models/credential/driver-ooj-credential.model';
import { AttributeTypeService } from '../../driver/services/attribute-type.service';
import { CredentialIssuanceApplicationService } from '../../driver/services/credential-issuance-application.service';
import { CredentialTypeAttributeTypeMapService } from '../../driver/services/credential-type-attribute-type-map.service';
import { DriverCredentialService } from '../../driver/services/driver-credential.service';
import { LicenseClassTypeService } from '../../driver/services/license-class-type.service';
import { IJurisdiction, Jurisdiction } from '../../foundation/models/enums/jurisdiction.enum';
import { BaseComponent } from '../../shared/base-components/base.component';

/**
 * Serves as the context for the customer search view modal dialog
 */
export class AddCredentialContext {
    constructor(
        public businessTransactionId: number,
        public id: number,
        public customerNumberId: number,
        public expirationDate?: Date,
        public issueDate?: Date
    ) {}
}

@Component({
    selector: 'add-credential-modal',
    templateUrl: './add-credential-modal.html'
})
export class AddCredentialModalComponent extends BaseComponent {
    private transactionNumber = 0;
    private oojCredential: DriverOOJCredential = new DriverOOJCredential();
    public form: UntypedFormGroup;
    public countries: any[] = [];
    private jurisdictions: IJurisdiction[] = [];
    private states: IJurisdiction[] = [];
    public licenseClassTypes: any[] = [];
    public endorsementTypes: any[] = [];
    public restrictionTypes: any[] = [];
    private credentialTypeAttributeTypeMaps: DriverCredentialTypeAttributeTypeMap[] = new Array();
    private driverAttributeTypeLookups: DriverAttributeTypeLookups;
    public id: number;
    public showButtonLoading: boolean;
    public showButton = false;
    // Constants
    private stateNonUsCode = 'Other';
    private stateNonUsId = 0;
    private usCode: number = new Jurisdiction().AZ.country;
    private azId: number = new Jurisdiction().AZ.id;

    constructor(
        private customerManageService: CustomerManageService,
        private driverCredentialService: DriverCredentialService,
        public dialogRef: MatDialogRef<AddCredentialModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: AddCredentialContext,
        private licenseClassTypeService: LicenseClassTypeService,
        private attributeTypeService: AttributeTypeService,
        private credentialTypeAttributeTypeMapService: CredentialTypeAttributeTypeMapService,
        private credentialIssuanceApplicationService: CredentialIssuanceApplicationService,
        formBuilder: UntypedFormBuilder,
        location: PlatformLocation
    ) {
        super();
        this.displayResults = false;
        this.transactionNumber = this.data.businessTransactionId;
        this.id = this.data.id;
        this.form = formBuilder.group({
            id: [0],
            credentialNumber: ['', Validators.required],
            travelId: [false],
            issueDate: [null, Validators.required],
            expirationDate: [null, Validators.required],
            stateId: [null],
            countryId: [null, Validators.required],
            licenseClassTypeIds: [[], Validators.required],
            restrictionTypeIds: [[]],
            endorsementTypeIds: [[]],
            imageCheck: [null],
            active: [true],
        }, {
            validators: Validators.compose([this.stateIdValidator, this.dateValidator])
        });

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    private stateIdValidator(group: UntypedFormGroup) {
        const stateId = group.get('stateId');
        const countryId = group.get('countryId');
        let countryEnum = new Country();

        // Must select a state if the country is US
        if (!!countryId && countryId.value == countryEnum.US.id && (!stateId || !stateId.value)) {
            return { 'stateIdRequired': true };
        }
    }

    private dateValidator(group: UntypedFormGroup) {
        const issueDate = group.get('issueDate');
        const expirationDate = group.get('expirationDate');

        if (!!issueDate.value && !!expirationDate.value && moment(expirationDate.value) < moment(issueDate.value)) {
            return { 'issueDateMustBeBeforeExpirationDate': true };
        }
    }

    public async ngOnInit() {
        await this.initPage();
        await this.getOojCredential(this.id);

        if ((this.id == null) || (this.id == 0)) {
            this.form.get('countryId').setValue(this.usCode);
        }

        this.displayResults = true;
    }

    /**
     * Initializes the page
     */
    private initPage = async (): Promise<void> => {
        this.licenseClassTypes = await this.getLicenseClasses();
        this.countries = new Country().GetActive();
        this.jurisdictions = await this.fetchJurisdictions();
        this.states = this.jurisdictions.filter(e => e.id != this.azId);
        this.states.push(({ id: this.stateNonUsId, code: this.stateNonUsCode }) as IJurisdiction);
        this.credentialTypeAttributeTypeMaps = await this.setDriverCredentialTypeEndorsementTypeMaps();
        this.driverAttributeTypeLookups = await this.getAttributeTypes();
    };

    private async fetchJurisdictions(): Promise<IJurisdiction[]> {
        return (await this.customerManageService.getIdentificationLookups().toPromise()).jurisdictions.map(x => ({
            id: x.id,
            country: x.countryId,
            code: x.jurisdictionCode,
            displayValue: x.jurisdictionDisplayValue,
            description: x.jurisdictionDisplayValue
        }) as IJurisdiction);
    }

    public getJurisdictions = (): IJurisdiction[] => this.states.filter(e => e.country == this.form.get('countryId').value);

    public countryChange = (): void => {
        let formControl = this.form.get('stateId');
        formControl.setValue(null);
        formControl.markAsPristine();
        formControl.markAsUntouched();
    };

    /**
     * Gets the OOJ Credential being edited, if that is relevant
     */
    private getOojCredential = async (id: number): Promise<void> => {
        if ((id == null) || (id <= 0)) {
            return;
        }

        try {
            let response = await this.driverCredentialService.getOojCredentialForBusinessTransaction(this.transactionNumber, id).pipe(first()).toPromise();

            if (response != null) {
                this.form.get('id').setValue(response.id);
                this.form.get('credentialNumber').setValue(response.credentialNumber);
                this.form.get('travelId').setValue(response.travelId);
                this.form.get('stateId').setValue(response.stateId);
                this.form.get('countryId').setValue(response.countryId);
                this.form.get('licenseClassTypeIds').setValue(response.licenseClassTypeIds);
                this.form.get('restrictionTypeIds').setValue(response.restrictionTypeIds);
                this.form.get('endorsementTypeIds').setValue(response.endorsementTypeIds);
                this.form.get('active').setValue(response.active);
                let issueDate = null;
                let issueDateMoment = moment(response.issueDate);
                let expirationDate = null;
                let expirationDateMoment = moment(response.expirationDate);

                if (issueDateMoment.isValid()) {
                    issueDate = issueDateMoment.format('MM/DD/YYYY');
                }

                if (expirationDateMoment.isValid()) {
                    expirationDate = expirationDateMoment.format('MM/DD/YYYY');
                }

                this.form.get('issueDate').setValue(issueDate);
                this.form.get('expirationDate').setValue(expirationDate);
                this.licenseClassTypeSelectionChanged();
            }
        } catch (e) {
            this.handleValidationResult(e);
        }
    };

    /**
     * Sets the driver credential type endorsement type maps
     */
    private setDriverCredentialTypeEndorsementTypeMaps = async (): Promise<any[]> => {
        try {
            let result = (await this.credentialTypeAttributeTypeMapService.getCredentialTypeAttributeMaps().pipe(first()).toPromise());

            if (null == result) {
                return null;
            }

            return result.driverCredentialTypeAttributeTypeMaps;
        } catch (e) {
            this.handleValidationResult(e);
        }
    };

    /**
     * Sets the license classes
     */
    private getLicenseClasses = async (): Promise<any[]> => {
        try {
            let response = await this.licenseClassTypeService.getLicenseClasses().pipe(first()).toPromise();
            let licenseClassTypes: any[] = [];

            for (var i = 0; i < response.driverLicenseClassTypes.length; i++) {
                licenseClassTypes.push(
                    {
                        label: response.driverLicenseClassTypes[i].driverLicenseClassTypeDisplayValue,
                        value: +response.driverLicenseClassTypes[i].id
                    });
            }

            return licenseClassTypes;
        } catch (e) {
            this.handleValidationResult(e);
        }
    };

    /**
     * Resets endorsements and restrictions based on new license class selections
     */
    public licenseClassTypeSelectionChanged = (): void => {
        this.endorsementTypes = this.getEndorsementTypes();
        this.restrictionTypes = this.getRestrictionTypes();
        this.reconcileSelectedAttributes();
    };

    private getEndorsementTypes = (): any[] => {
        let selectedCredentialTypes = this.form.get('licenseClassTypeIds').value;
        let selectedCredentialTypeMaps = this.credentialTypeAttributeTypeMaps.filter(e => selectedCredentialTypes.some(i => i == e.driverLicenseClassTypeId));
        return this.driverAttributeTypeLookups.lookups.endorsementTypes.filter(e => selectedCredentialTypeMaps.some(i => i.driverAttributeTypeId == e.value))
            .map(e => <any> {
                label: e.text,
                value: e.value
            });
    };

    private getRestrictionTypes = (): any[] => {
        let selectedCredentialTypes = this.form.get('licenseClassTypeIds').value;
        let selectedCredentialTypeMaps = this.credentialTypeAttributeTypeMaps.filter(e => selectedCredentialTypes.some(i => i == e.driverLicenseClassTypeId));
        return this.driverAttributeTypeLookups.lookups.restrictionTypes.filter(e => selectedCredentialTypeMaps.some(i => i.driverAttributeTypeId == e.value))
            .map(e => <any> {
                label: e.text,
                value: e.value
            });
    };

    /**
     * Calls the endorsement type service to retrieve the lookups
     */
    private getAttributeTypes = async (): Promise<any> => {
        try {
            let result = await this.attributeTypeService.getAttributeTypeLookup().pipe(first()).toPromise();
            return result;
        } catch (e) {
            this.handleValidationResult(e);
        }
    };

    /**
     * Unselectes things that are no longer selectable
     */
    private reconcileSelectedAttributes = (): void => {
        let selectedEndorsementTypes = this.form.get('endorsementTypeIds').value;
        let validEndorsementTypes = this.getEndorsementTypes();
        let invalidEndorsementSelections = selectedEndorsementTypes.filter(e => false == validEndorsementTypes.some(e2 => e == e2.value));

        // check to see if some selected endorsement type ids do not exist
        if (0 != invalidEndorsementSelections.length) {
            invalidEndorsementSelections.forEach(e => selectedEndorsementTypes.remove(e));
            this.form.get('endorsementTypeIds').setValue(selectedEndorsementTypes);
        }

        let selectedRestrictionTypes = this.form.get('endorsementTypeIds').value;
        let validRestrictionTypes = this.getRestrictionTypes();
        let invalidRestrictionSelections = selectedRestrictionTypes.filter(e => false == validRestrictionTypes.some(e2 => e == e2.value));

        // check to see if some selected endorsement type ids do not exist
        if (0 != invalidRestrictionSelections.length) {
            invalidRestrictionSelections.forEach(e => selectedRestrictionTypes.remove(e));
            this.form.get('endorsementTypeIds').setValue(selectedRestrictionTypes);
        }
    };

    /**
     * Closes the modal dialog with no return result
     */
    public cancel() {
        this.dialogRef.close();
    }

    /**
     * Saves the OOJ credential to WIP and OOJ table
     */
    public save = (): void => {
        if (true != this.form.valid) {
            return;
        }

        let driverOOJCredentialRequest = new DriverOOJCredentialRequest();
        driverOOJCredentialRequest.businessTransactionId = this.transactionNumber;
        driverOOJCredentialRequest.driverOOJCredential = new DriverOOJCredential();
        driverOOJCredentialRequest.driverOOJCredential.id = this.form.get('id').value;
        driverOOJCredentialRequest.driverOOJCredential.credentialNumber = this.form.get('credentialNumber').value;
        driverOOJCredentialRequest.driverOOJCredential.travelId = this.form.get('travelId').value;
        driverOOJCredentialRequest.driverOOJCredential.stateId = this.form.get('stateId').value;
        driverOOJCredentialRequest.driverOOJCredential.countryId = this.form.get('countryId').value;
        driverOOJCredentialRequest.driverOOJCredential.licenseClassTypeIds = this.form.get('licenseClassTypeIds').value;
        driverOOJCredentialRequest.driverOOJCredential.restrictionTypeIds = this.form.get('restrictionTypeIds').value;
        driverOOJCredentialRequest.driverOOJCredential.endorsementTypeIds = this.form.get('endorsementTypeIds').value;
        driverOOJCredentialRequest.driverOOJCredential.issueDate = this.form.get('issueDate').value;
        driverOOJCredentialRequest.driverOOJCredential.expirationDate = this.form.get('expirationDate').value;
        driverOOJCredentialRequest.executeImageCheck = this.form.get('imageCheck').value == true;

        this.showButton = false;

        if (this.transactionNumber > 0) {
            this.credentialIssuanceApplicationService.saveOOJCredential(driverOOJCredentialRequest)
                .subscribe(() => this.dialogRef.close(true),
                    e => this.handleValidationResult(e));
        } else {
            this.credentialIssuanceApplicationService.saveOOJCredentialNoTransaction(driverOOJCredentialRequest.driverOOJCredential, this.data.customerNumberId)
                .subscribe(() => this.dialogRef.close(true),
                    e => this.handleValidationResult(e));
        }
        this.showButton = true;
    };
}
